import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Wrench } from 'lucide-react';

const WorkerReportTime = () => {
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [machines, setMachines] = useState([]);
  const [formData, setFormData] = useState({
    project_id: '',
    employee_id: '',
    date: new Date().toISOString().split('T')[0],
    hours: '',
    machine_id: '',
    machine_hours: '',
    comment: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, employeesRes, machinesRes] = await Promise.all([
        api.getProjects(),
        api.getEmployees(),
        api.getMachines().catch(() => ({ data: [] })),
      ]);
      setProjects(projectsRes.data.filter(p => p.status === 'active'));
      setEmployees(employeesRes.data);
      setMachines(machinesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        project_id: formData.project_id,
        employee_id: formData.employee_id,
        date: formData.date,
        hours: parseFloat(formData.hours),
        comment: formData.comment,
        // Simon-regel: maskin absorberar labor på fakturan
        machine_id: formData.machine_id || null,
        machine_hours: formData.machine_id && formData.machine_hours
          ? parseFloat(formData.machine_hours)
          : (formData.machine_id ? parseFloat(formData.hours) : null),
      };
      await api.createTimeReport(data);
      toast.success('Tidrapport skickad!');
      setFormData({
        project_id: '',
        employee_id: '',
        date: new Date().toISOString().split('T')[0],
        hours: '',
        machine_id: '',
        machine_hours: '',
        comment: '',
      });
    } catch (error) {
      console.error('Failed to submit time report:', error);
      toast.error('Kunde inte skicka tidrapport');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6" data-testid="worker-report-time-page">
      <div className="bg-card rounded-xl shadow-sm border border-border p-6 max-w-2xl mx-auto">
        <h2 className="text-xl font-semibold text-foreground mb-6" style={{ fontFamily: 'Work Sans' }}>Rapportera tid</h2>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label>Projekt *</Label>
            <Select value={formData.project_id} onValueChange={(value) => setFormData({ ...formData, project_id: value })}>
              <SelectTrigger data-testid="select-project-worker">
                <SelectValue placeholder="Välj projekt" />
              </SelectTrigger>
              <SelectContent>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Anställd *</Label>
            <Select value={formData.employee_id} onValueChange={(value) => setFormData({ ...formData, employee_id: value })}>
              <SelectTrigger data-testid="select-employee-worker">
                <SelectValue placeholder="Välj anställd" />
              </SelectTrigger>
              <SelectContent>
                {employees.map(e => (
                  <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="date">Datum *</Label>
            <Input
              id="date"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              data-testid="time-date-input"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="hours">Timmar *</Label>
            <Input
              id="hours"
              type="number"
              step="0.5"
              placeholder="8"
              value={formData.hours}
              onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
              data-testid="time-hours-input"
              required
            />
          </div>

          {/* Maskin-val (valfritt) */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <Wrench size={12} /> Maskin (valfri)
            </Label>
            <select
              value={formData.machine_id}
              onChange={(e) => setFormData({ ...formData, machine_id: e.target.value })}
              className="w-full h-10 px-3 border border-input rounded-md text-sm bg-background"
              data-testid="report-machine-select"
            >
              <option value="">Ingen maskin</option>
              {machines.map(m => (
                <option key={m.id} value={m.id}>
                  {m.name}{m.registration_no ? ` (${m.registration_no})` : ''}
                  {m.hourly_rate ? ` — ${m.hourly_rate} kr/h` : ''}
                </option>
              ))}
            </select>
            {formData.machine_id && (
              <p className="text-[11px] text-[#E8613A] font-medium">
                Maskinens timtaxa ersätter arbetstaxan på fakturan (svensk anläggningspraxis).
                Din lön beräknas separat på arbetstimmarna.
              </p>
            )}
          </div>

          {formData.machine_id && (
            <div className="space-y-2">
              <Label htmlFor="machine_hours">Maskintimmar (valfri)</Label>
              <Input
                id="machine_hours"
                type="number"
                step="0.5"
                placeholder={formData.hours || 'Samma som arbetstimmar'}
                value={formData.machine_hours}
                onChange={(e) => setFormData({ ...formData, machine_hours: e.target.value })}
                data-testid="report-machine-hours-input"
              />
              <p className="text-[11px] text-muted-foreground">
                Lämna tomt så används arbetstimmarna ({formData.hours || '—'}).
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="comment">Kommentar</Label>
            <textarea
              id="comment"
              className="w-full min-h-[80px] px-3 py-2 border border-border rounded-md"
              placeholder="Valfri kommentar"
              value={formData.comment}
              onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
              data-testid="time-comment-input"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="submit-time-report-btn"
            className="w-full bg-orange-500 hover:bg-orange-600 py-6"
          >
            {loading ? 'Skickar...' : 'Skicka tidrapport'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default WorkerReportTime;
